// src/pages/Home.tsx
import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Grid,
  useTheme,
  Avatar,
  Paper,
} from "@mui/material";

import {
  FavoriteBorder,
  PersonSearch,
  LockOpen,
  DirectionsBoat,
  SettingsSuggest,
  EventNote,
  ArrowForward,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

interface Category {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  path: string;
}

const allCategories: Category[] = [
  {
    title: "Wishlist",
    subtitle: "Manage client favorites",
    icon: <FavoriteBorder fontSize="inherit" />,
    path: "/wishlist",
  },
  {
    title: "Prospects",
    subtitle: "Track potential clients",
    icon: <PersonSearch fontSize="inherit" />,
    path: "/prospects",
  },
  {
    title: "Fournisseurs",
    subtitle: "Track suppliers",
    icon: <PersonSearch fontSize="inherit" />,
    path: "/fournisseurs",
  },
  {
    title: "Access Management",
    subtitle: "Control user permissions",
    icon: <LockOpen fontSize="inherit" />,
    path: "/access",
  },
  {
    title: "Models",
    subtitle: "Browse boat details",
    icon: <DirectionsBoat fontSize="inherit" />,
    path: "/models",
  },
  {
    title: "Model Options",
    subtitle: "Configure features",
    icon: <SettingsSuggest fontSize="inherit" />,
    path: "/options",
  },
  {
    title: "Boat Show",
    subtitle: "Events & Exhibitions",
    icon: <DirectionsBoat fontSize="inherit" />,
    path: "/boatshow",
  },
  {
    title: "Schedules",
    subtitle: "Manage appointments",
    icon: <EventNote fontSize="inherit" />,
    path: "/boatshow-schedule",
  },
];

// Catégories accessibles par les agents
const agentCategories = [
  "/wishlist",
  "/prospects",
  "/boatshow-schedule",
  "/fournisseurs",
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Récupérer le rôle de l'utilisateur et filtrer les catégories
  const categories = useMemo(() => {
    const userRole = localStorage.getItem("role");
    if (userRole === "agent") {
      return allCategories.filter((cat) => agentCategories.includes(cat.path));
    }
    return allCategories;
  }, []);

  const userName = localStorage.getItem("fullname") || "User"; // Assurez-vous d'avoir stocké le nom quelque part lors du login

  return (
    <Box sx={{ minHeight: "100vh", py: 4 }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box mb={6} textAlign="center">
          <Typography
            variant="overline"
            sx={{
              color: "#C4B08C",
              fontWeight: 800,
              letterSpacing: 2,
              fontSize: "0.9rem",
            }}
          >
            DASHBOARD
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: "#1a1a1a",
              mb: 1,
              fontFamily: "Playfair Display, serif", // Si disponible ou fallback serif
            }}
          >
            Welcome to WAMA
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto" }}
          >
            Select a module below to start managing your boat shows, prospects,
            and configurations.
          </Typography>
        </Box>

        {/* Cards Grid */}
        <Grid container spacing={3} justifyContent="center">
          {categories.map((cat, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={cat.title}>
              <Card
                onClick={() => navigate(cat.path)}
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  borderRadius: 4,
                  border: "1px solid rgba(0,0,0,0.03)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.02)",
                  transition:
                    "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                  bgcolor: "#ffffff",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 20px 40px rgba(196, 176, 140, 0.15)", // Ombre #C4B08C
                    borderColor: "rgba(196, 176, 140, 0.3)",
                    "& .icon-box": {
                      bgcolor: "#C4B08C",
                      color: "white",
                      transform: "scale(1.1) rotate(5deg)",
                    },
                    "& .arrow-icon": {
                      transform: "translateX(5px)",
                      opacity: 1,
                      color: "#C4B08C",
                    },
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 4,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  {/* Icon Box */}
                  <Box
                    className="icon-box"
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: "24px",
                      bgcolor: "rgba(196, 176, 140, 0.08)",
                      color: "#C4B08C",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "2rem",
                      mb: 3,
                      transition: "all 0.4s ease",
                    }}
                  >
                    {cat.icon}
                  </Box>

                  {/* Text Content */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      textAlign: "center",
                      mb: 1,
                      color: "#2c2c2c",
                    }}
                  >
                    {cat.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", mb: 2, px: 2 }}
                  >
                    {cat.subtitle}
                  </Typography>

                  {/* Arrow Indicator */}
                  <Box
                    sx={{ mt: "auto", display: "flex", alignItems: "center" }}
                  >
                    <ArrowForward
                      className="arrow-icon"
                      sx={{
                        opacity: 0,
                        transition: "all 0.3s ease",
                        transform: "translateX(-10px)",
                        fontSize: "1.2rem",
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default HomePage;
